import Compressor from 'compressorjs';
/**
 * Function to compress a file with validation and fallback
 * If compression produces a suspicious file size, returns the original file
 * @param file - Original file to compress
 * @param maxWidth - Maximum width for compressed image (default: 500)
 * @param quality - Compression quality 0-1 (default: 0.8)
 * @returns compressed file or original file if compression fails validation
 */
export const compressFile = (file, maxWidth = 500, quality = 0.8) => {
    return new Promise(resolve => {
        const originalSize = file.size;
        new Compressor(file, {
            maxWidth: Math.min(maxWidth, 4096), // Canvas-safe limit for iOS/mobile
            quality,
            success: async (blob) => {
                const compressedFile = new File([blob], file.name, {
                    type: blob.type,
                });
                // Step 1: Check compression ratio (instant)
                // Normal compression should be 10-110% of original size
                const ratio = compressedFile.size / originalSize;
                if (ratio < 0.1 || ratio > 1.1) {
                    console.warn('Compression ratio validation failed, using original', {
                        originalSize,
                        compressedSize: compressedFile.size,
                        ratio: ratio.toFixed(2),
                        fileName: file.name,
                    });
                    resolve(file);
                    return;
                }
                // Step 2: Validate image dimensions (detects corruption/blank images)
                const isValid = await new Promise(resolveValidation => {
                    const img = new Image();
                    const url = URL.createObjectURL(compressedFile);
                    img.onload = () => {
                        URL.revokeObjectURL(url);
                        // If dimensions are 0, image is corrupted
                        const valid = img.naturalWidth > 0 && img.naturalHeight > 0;
                        if (!valid) {
                            console.warn('Image dimension validation failed (corrupted)', {
                                naturalWidth: img.naturalWidth,
                                naturalHeight: img.naturalHeight,
                                fileName: file.name,
                            });
                        }
                        resolveValidation(valid);
                    };
                    img.onerror = () => {
                        URL.revokeObjectURL(url);
                        console.warn('Image load validation failed (corrupted)', {
                            fileName: file.name,
                        });
                        resolveValidation(false);
                    };
                    img.src = url;
                });
                if (!isValid) {
                    console.warn('Compression validation failed, using original', {
                        originalSize,
                        compressedSize: compressedFile.size,
                        fileName: file.name,
                    });
                    resolve(file);
                    return;
                }
                // All validations passed
                resolve(compressedFile);
            },
            error: err => {
                console.error('Compression error, using original', {
                    error: err.message,
                    originalSize,
                    fileName: file.name,
                });
                resolve(file); // Fallback to original on error
            },
        });
    });
};
